import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const ContactUs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '' // honeypot: hidden from real users; bots fill it -> we drop the submission
  });
  const [status, setStatus] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  // Record when the form mounted; submissions faster than ~3s are almost always bots.
  const [mountedAt] = useState(() => Date.now());

  // For logged-in users, pre-populate name/email from their account (shown read-only below).
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus('');
    setErrorMsg('');

    // Client-side quick checks (the server re-validates authoritatively).
    if ((formData.message || '').trim().length < 15) {
      setStatus('error');
      setErrorMsg('Please enter a real message (at least 15 characters).');
      setLoading(false);
      return;
    }
    // Too-fast submit (bot) — honeypot + timing are enforced server-side too.
    const elapsed = Date.now() - mountedAt;

    try {
      const apiUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:3000' 
        : 'https://www.gandalstock.com';
      
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, elapsedMs: elapsed })
      });

      if (response.ok) {
        setStatus('success');
        // Preserve the account name/email for logged-in users; only clear the message fields.
        setFormData({
          name: user ? (user.name || '') : '',
          email: user ? (user.email || '') : '',
          subject: '',
          message: '',
          website: ''
        });
      } else {
        let msg = 'Sorry, there was an error sending your message. Please try again.';
        try { const d = await response.json(); if (d && d.error) msg = d.error; } catch (_) { /* ignore */ }
        setStatus('error');
        setErrorMsg(msg);
      }
    } catch (error) {
      setStatus('error');
      setErrorMsg('Sorry, there was an error sending your message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">Contact Us</h1>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                readOnly={!!user}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${user ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                readOnly={!!user}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${user ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
              />
              {user && (
                <p className="mt-1 text-xs text-gray-500">
                  From your account. Update it in your profile if it’s changed.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="6"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Honeypot: visually hidden + removed from tab order & a11y tree. Real
                users never fill this; bots do, and the server drops those submissions. */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 'auto', height: 0, width: 0, overflow: 'hidden' }}>
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/')}
              className="w-full bg-gray-500 text-white py-3 px-4 rounded-md hover:bg-gray-600"
            >
              Go Home
            </button>
          </form>

          {status === 'success' && (
            <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
              <p className="mb-3">Thank you for your message! We'll get back to you soon.</p>
              <button
                onClick={() => navigate('/')}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Back to Home
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {errorMsg || 'Sorry, there was an error sending your message. Please try again.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
